import { backgroundColorState, greyMediumState, negativeState, newbackgroundColorState, popoverContentState, positiveState, textColorState, themeState, whiteMediumState } from "@/lib/store";
import { Box, Card, CardContent, CardHeader, IconButton, Popover, Skeleton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import InfoIcon from "@mui/icons-material/Info";
import { useAtomValue } from "jotai";

import styles from "./TickertapePopover.module.scss";
import { useEffect, useMemo, useState } from "react";
import { getStockInfoOnServer } from "@/controllers/stock";
import { isEmpty } from "lodash";
import dayjs from "dayjs";
import { classNames, formatDate, getTimeDifference, nFormatter } from "@/lib/utils";
import { brighten } from "@/lib/ortex-charts/color";
import type { TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";
import dynamic from "next/dynamic";
import Link from "next/link";

// The trend chart renders only once a ticker is clicked, so it and the ortex-charts packages load on first open instead of in the shared _app bundle.
const TimeSeriesChart = dynamic(() => import("@/components/ortex-charts/TimeSeriesChart"), {
    ssr: false,
    loading: ({ error }) => (error ? null : <Skeleton variant="rounded" width="100%" height="100%" />),
});

const TickertapePopover = ({ instrument, anchorEl, handleClose }) => {
    const theme = useAtomValue(themeState);

    const backgroundColor = useAtomValue(backgroundColorState);
    const popoverContentBackground = useAtomValue(popoverContentState);
    const textColor = useAtomValue(textColorState);
    const greyMedium = useAtomValue(greyMediumState);
    const negative = useAtomValue(negativeState);
    const positive = useAtomValue(positiveState);
    const whiteMedium = useAtomValue(whiteMediumState);
    const newbackgroundColor = useAtomValue(newbackgroundColorState);

    const secondaryColor = theme === "dark" ? brighten(textColor, -0.1) : brighten(greyMedium, 0.1);

    const isMobile = useMediaQuery("(max-width: 1024px)");

    const [stockData, setStockData] = useState<any>(null);

    const [trend, setTrend] = useState<[number, number][]>([]);

    const timeDiff = getTimeDifference(instrument?.[1].timestamp);

    useEffect(() => {
        if (instrument && instrument[1].exchange && instrument[1].ticker) {
            getStockInfoOnServer(instrument[1].exchange as string, instrument[1].ticker as string, true).then((res) => {
                if (!isEmpty(res)) {
                    setStockData(res);
                    setTrend(res.price_trend ?? []);
                }
            });
        } else {
            setStockData(null);
            setTrend([]);
        }
    }, [instrument?.[0]]);

    const percentageVariationColor = (percentage: number) => {
        if (percentage > 0) {
            return positive;
        } else if (percentage < 0) {
            return negative;
        } else {
            return textColor;
        }
    };

    const percentageVariationArrow = (percentage: number) => {
        if (percentage > 0) {
            return <ArrowDropUpIcon sx={{ color: positive, fontSize: "16px" }} />;
        } else if (percentage < 0) {
            return <ArrowDropDownIcon sx={{ color: negative, fontSize: "16px" }} />;
        } else return;
    };

    const removeMinusSign = (percentage: number) => {
        if (percentage < 0) {
            return percentage * -1;
        } else return percentage;
    };

    const trendSeries = useMemo<TimeSeries[]>(() => [{ id: "trend", title: "Price", type: "spline", data: trend, color: "#31ABA6", precision: 2, legend: false }], [trend]);

    const popoverContent = (
        <Card className={styles.card} sx={{ color: textColor }}>
            <CardHeader
                className={styles.header}
                sx={{ backgroundColor: theme === "dark" ? backgroundColor : whiteMedium }}
                title={
                    <Box className={styles["space-between"]}>
                        <Box className={styles.title}>
                            <Typography className={styles.stockName}>{instrument?.[1].name}</Typography>
                            <Typography className={styles.stockTicker} sx={{ color: secondaryColor }}>
                                {instrument?.[0]}
                            </Typography>
                        </Box>
                        {timeDiff[1] < 0 ||
                            timeDiff[1] > 20 ||
                            (timeDiff[0] !== 0 && (
                                <Tooltip arrow title={`Last updated: ${dayjs(instrument?.[1].timestamp).format("DD MMM HH:mm")}`}>
                                    <InfoIcon sx={{ fontSize: "14px", marginLeft: 0.5 }} />
                                </Tooltip>
                            ))}
                    </Box>
                }
                subheader={
                    <Box className={classNames(styles.subtitle, styles["space-between"])} sx={{ color: secondaryColor }}>
                        {stockData &&
                            (stockData?.sector?.length > 1 ? (
                                <>
                                    <Typography variant="body2" component="p" sx={{ fontWeight: 600 }}>
                                        {stockData.sector[0]}
                                    </Typography>
                                    <ArrowForwardIcon sx={{ color: textColor, fontSize: "16px" }} />
                                    <Typography variant="body2" component="p">
                                        {stockData.sector[stockData.sector.length - 1]}
                                    </Typography>
                                </>
                            ) : (
                                stockData?.sector?.[0]
                            ))}
                    </Box>
                }
                action={
                    instrument?.[1].exchange && instrument?.[1].ticker ? (
                        <IconButton aria-label="Launch" onClick={handleClose}>
                            <Link href={`/s/${instrument?.[1].exchange}/${instrument?.[1].ticker}`} target={isMobile ? "_self" : "_blank"}>
                                <LaunchRoundedIcon sx={{ color: textColor }} />
                            </Link>
                        </IconButton>
                    ) : null
                }
                disableTypography
            ></CardHeader>
            <CardContent className={styles.content} sx={{ backgroundColor: theme === "dark" ? popoverContentBackground : newbackgroundColor }}>
                <Box className={styles.stockData}>
                    <Box className={styles.price}>
                        <Box className={styles.lastPrice}>
                            <Typography variant="body2" component="p" className={styles.contentSubtitle} sx={{ color: secondaryColor }}>
                                Last Close
                            </Typography>
                            <Typography variant="body2" component="p">
                                {instrument?.[1]?.lastPrice?.toFixed(2) || "N/A"}
                            </Typography>
                        </Box>
                        <Box className={styles.priceTrend}>
                            <Typography variant="body2" component="p" className={styles.contentSubtitle} sx={{ color: secondaryColor }}>
                                Price Trend: 1 YR
                            </Typography>
                            <Box className={styles.chartContainer}>
                                <TimeSeriesChart
                                    series={trendSeries}
                                    timeAxis={false}
                                    grid={false}
                                    legend={false}
                                    interactive={false}
                                    rangeButtons={false}
                                    exportMenu={false}
                                    axes={[{ id: "right", visible: false }]}
                                    tooltipTitle={(t) => formatDate(dayjs(t))}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Box className={styles.market}>
                        <Box className={styles.row}>
                            <Box className={styles.profits}>
                                <Typography variant="body2" component="p" sx={{ color: secondaryColor }}>
                                    1M Return
                                </Typography>
                                <Box className={styles.priceVariation} sx={{ color: percentageVariationColor(stockData?.["1m_return"]) }}>
                                    {percentageVariationArrow(stockData?.["1m_return"])}
                                    <Typography variant="body2" component="p">
                                        {(stockData && removeMinusSign(stockData["1m_return"]) + "%") || "N/A"}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className={styles.profits}>
                                <Typography variant="body2" component="p" sx={{ color: secondaryColor }}>
                                    1Y Return
                                </Typography>
                                <Box className={styles.priceVariation} sx={{ color: percentageVariationColor(stockData?.["1y_return"]), justifyContent: "flex-end" }}>
                                    {percentageVariationArrow(stockData?.["1y_return"])}
                                    <Typography variant="body2" component="p">
                                        {(stockData && removeMinusSign(stockData["1y_return"]) + "%") || "N/A"}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box className={styles.row}>
                            <Box className={styles.profits}>
                                <Typography variant="body2" component="p" sx={{ color: secondaryColor }}>
                                    52W High
                                </Typography>
                                <Typography variant="body2" component="p">
                                    {(stockData && nFormatter(stockData?.high_52_week, 2)) || "N/A"}
                                </Typography>
                            </Box>
                            <Box className={styles.profits}>
                                <Typography variant="body2" component="p" sx={{ color: secondaryColor }}>
                                    52W Low
                                </Typography>
                                <Typography variant="body2" component="p">
                                    {(stockData && nFormatter(stockData?.low_52_week, 2)) || "N/A"}
                                </Typography>
                            </Box>
                        </Box>
                        <Box className={styles.row}>
                            <Box className={styles.profits}>
                                <Typography variant="body2" component="p" sx={{ color: secondaryColor }}>
                                    Market Cap
                                </Typography>
                                <Typography variant="body2" component="p">
                                    {(stockData && nFormatter(stockData?.mkt_cap_usd, 2)) || "N/A"}
                                </Typography>
                            </Box>
                            <Box className={styles.profits}>
                                <Typography variant="body2" component="p" sx={{ color: secondaryColor }}>
                                    Volume
                                </Typography>
                                <Typography variant="body2" component="p">
                                    {(stockData && nFormatter(stockData?.volume_3m_this_exchange, 2)) || nFormatter(stockData?.volume_3m_usd, 2) || "N/A"}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Box></Box>
            </CardContent>
        </Card>
    );

    return (
        <Popover
            open={instrument !== null}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            sx={{
                "& .MuiBackdrop-root": {
                    backgroundColor: "transparent",
                },
            }}
            slotProps={{
                paper: {
                    sx: {
                        p: 0,
                        bgcolor: "transparent",
                        width: "380px",
                        boxShadow: 3,
                        borderRadius: "4px",
                    },
                },
            }}
        >
            {instrument && popoverContent}
        </Popover>
    );
};

export default TickertapePopover;
